import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { ChevronUp, ChevronDown, MessageSquare, Send, Plus, X, ArrowLeft, Users, Search } from "lucide-react";
import { useAuth } from "@shared/lib/auth";
import { useIsMobile } from "@shared/hooks/useIsMobile";
import { useMessagingPolling } from "../../lib/useMessagingPolling";
import { getConversationTitle } from "@shared/lib/conversationTitle";
import { useDesktopMessaging } from "../../hooks/useDesktopMessaging";
import type { Conversation } from "@shared/lib/types";
import type { DesktopRecipientType } from "../../hooks/useDesktopMessaging";

type SearchResult = {
  type: DesktopRecipientType;
  id: number;
  label: string;
  subtitle: string;
};

export function MessagingPopup() {
  const auth = useAuth();
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);

  // Check auth status - but don't return early before hooks!
  const isAuthenticated = auth.status === "authenticated" && auth.user;

  // Unread count polling (shared with MobileAdminLayout badge logic)
  const { unreadCount, refresh: refreshUnreadCount } = useMessagingPolling(Boolean(isAuthenticated));

  const isAdmin = auth.user?.role === "admin";
  const isDirector = auth.user?.role === "director";
  const isTeacher = auth.user?.role === "teacher";
  const canCompose = isAdmin || isDirector || isTeacher;
  const {
    conversations,
    loading,
    view,
    setView,
    activeConversation,
    setActiveConversation,
    messages,
    replyBody,
    setReplyBody,
    sending,
    messageType,
    setMessageType,
    setSelectedChildId,
    setSelectedParentUserId,
    selectedRecipientType,
    setSelectedRecipientType,
    selectedRecipientLabel,
    setSelectedRecipientLabel,
    selectedClassroomId,
    setSelectedClassroomId,
    composeBody,
    setComposeBody,
    composeSending,
    composeError,
    children,
    parents,
    classrooms,
    composeDataLoading,
    searchQuery,
    setSearchQuery,
    showSearchResults,
    setShowSearchResults,
    selectConversation,
    sendReply,
    sendComposedMessage,
    openCompose,
  } = useDesktopMessaging({
    enabled: Boolean(isAuthenticated && isOpen),
    canCompose,
    onConversationsRefresh: refreshUnreadCount,
    onConversationRead: refreshUnreadCount,
  });

  // Filter children/classrooms for teachers (admins and directors see all)
  const filteredChildren = useMemo(() => {
    if (isAdmin || isDirector) return children;
    const assignedIds = auth.assignedClassroomIds ?? [];
    return children.filter((c) => assignedIds.includes(c.classroom_id));
  }, [children, isAdmin, isDirector, auth.assignedClassroomIds]);

  const filteredClassrooms = useMemo(() => {
    if (isAdmin || isDirector) return classrooms;
    const assignedIds = auth.assignedClassroomIds ?? [];
    return classrooms.filter((c) => assignedIds.includes(c.id));
  }, [classrooms, isAdmin, isDirector, auth.assignedClassroomIds]);

  // Build search results from children + parents
  const searchResults = useMemo((): SearchResult[] => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return [];

    const results: SearchResult[] = [];

    // Search children
    for (const child of filteredChildren) {
      const name = `${child.first_name} ${child.last_name}`.toLowerCase();
      if (name.includes(q)) {
        const linkedParent = parents.find((p) =>
          (p.children ?? []).some((c) => c.id === child.id)
        );
        const parentName = linkedParent
          ? linkedParent.full_name ?? `${linkedParent.first_name ?? ""} ${linkedParent.last_name ?? ""}`.trim()
          : null;
        results.push({
          type: "child",
          id: child.id,
          label: `${child.first_name} ${child.last_name}`,
          subtitle: parentName ? `Parent: ${parentName}` : "Child",
        });
      }
    }

    // Search parents
    for (const parent of parents) {
      const name = (parent.full_name ?? `${parent.first_name ?? ""} ${parent.last_name ?? ""}`).toLowerCase();
      if (name.includes(q)) {
        const childNames = (parent.children ?? []).map((c) => `${c.first_name} ${c.last_name}`).join(", ");
        results.push({
          type: "parent",
          id: parent.id,
          label: parent.full_name ?? `${parent.first_name ?? ""} ${parent.last_name ?? ""}`.trim(),
          subtitle: childNames ? `Parent of ${childNames}` : "Parent",
        });
      }
    }

    return results.slice(0, 10);
  }, [searchQuery, filteredChildren, parents]);

  // Close search dropdown on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [setShowSearchResults]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Listen for custom event to open compose mode (optionally with pre-selected recipient)
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as
        | { recipientId: number; recipientType: "parent" | "employee"; recipientName: string }
        | undefined;
      setIsOpen(true);
      setView("compose");
      setActiveConversation(null);

      if (detail) {
        if (detail.recipientType === "parent") {
          setMessageType("child");
          setSelectedParentUserId(detail.recipientId);
          setSelectedRecipientType("parent");
          setSelectedRecipientLabel(detail.recipientName);
        } else if (detail.recipientType === "employee") {
          // For employee messaging, open compose without pre-selecting
          // (employees are staff-to-staff, uses different flow)
          setMessageType("child");
        }
      }
    };
    window.addEventListener("messaging:compose", handler);
    return () => window.removeEventListener("messaging:compose", handler);
  }, [
    setActiveConversation,
    setMessageType,
    setSelectedParentUserId,
    setSelectedRecipientLabel,
    setSelectedRecipientType,
    setView,
  ]);

  // Auto-resize textarea
  const handleTextareaInput = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      textarea.style.height = `${Math.min(textarea.scrollHeight, 80)}px`;
    }
  }, []);

  const handleSendReply = async () => {
    const sent = await sendReply();
    if (sent && textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
  };

  const viewerRole = auth.user?.role ?? "admin";
  const titleFor = (conv: Conversation) => getConversationTitle(conv, viewerRole as "parent" | "teacher" | "admin" | "director");

  // Don't render for non-authenticated users (after all hooks have been called)
  if (!isAuthenticated) {
    return null;
  }

  // On mobile, full-page /messaging route replaces this popup
  if (isMobile) {
    return null;
  }

  const isExpandedView = view === "compose";

  return (
    <div className="fixed bottom-6 left-6 z-40">
      {/* Collapsed state - chevron with text */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="flex items-center gap-2 rounded-xl bg-surface/90 px-4 py-2.5 shadow-soft backdrop-blur-xl transition-all duration-200 hover:shadow-md hover:-translate-y-0.5"
        >
          <span className="text-sm font-light tracking-wide text-foreground">Messaging</span>
          <div className="relative">
            <ChevronUp className="h-4 w-4 text-muted" />
            {unreadCount > 0 && (
              <span className="absolute -right-2 -top-2 flex h-4 w-4 items-center justify-center rounded-full bg-accent text-[10px] font-medium text-accent-foreground">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </div>
        </button>
      )}

      {/* Expanded state */}
      {isOpen && (
        <div className="w-80 rounded-xl bg-surface/95 shadow-soft backdrop-blur-xl overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-accent" />
              <span className="text-sm font-medium text-foreground">Messaging</span>
              {unreadCount > 0 && (
                <span className="rounded-full bg-accent px-1.5 py-0.5 text-[10px] font-medium text-accent-foreground">
                  {unreadCount}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {canCompose && view === "conversations" && (
                <button
                  onClick={openCompose}
                  className="rounded-lg p-1.5 text-muted transition-colors hover:bg-foreground/5 hover:text-foreground"
                  title="New Message"
                >
                  <Plus className="h-4 w-4" />
                </button>
              )}
              <button
                onClick={() => {
                  setIsOpen(false);
                  setActiveConversation(null);
                  setView("conversations");
                }}
                className="rounded-lg p-1.5 text-muted transition-colors hover:bg-foreground/5 hover:text-foreground"
              >
                <ChevronDown className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className={`${isExpandedView ? "h-[28rem]" : "h-72"} overflow-hidden transition-all duration-200`}>
            {view === "conversations" ? (
              // Conversations list
              <div className="h-full overflow-y-auto">
                {loading ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-border border-t-accent" />
                  </div>
                ) : conversations.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full px-4 text-center">
                    <MessageSquare className="h-8 w-8 text-muted mb-2" />
                    <p className="text-sm text-muted">No conversations yet</p>
                    {canCompose && (
                      <button
                        onClick={openCompose}
                        className="mt-2 text-xs text-accent hover:underline"
                      >
                        Start a conversation
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="divide-y divide-border">
                    {conversations.slice(0, 5).map((conv) => (
                      <button
                        key={conv.conversation_key}
                        onClick={() => selectConversation(conv.conversation_key)}
                        className="w-full px-4 py-3 text-left transition hover:bg-surface2"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-foreground truncate">
                                {titleFor(conv)}
                              </span>
                              {conv.unread_count > 0 && (
                                <span className="shrink-0 rounded-full bg-accent px-1.5 py-0.5 text-[10px] text-accent-foreground">
                                  {conv.unread_count}
                                </span>
                              )}
                            </div>
                            <p className="mt-0.5 text-xs text-muted truncate">
                              {conv.latest_message.body}
                            </p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : view === "thread" ? (
              // Active conversation
              <div className="flex flex-col h-full">
                {/* Back button and title */}
                <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
                  <button
                    onClick={() => {
                      setActiveConversation(null);
                      setView("conversations");
                    }}
                    className="rounded-lg p-1 text-muted hover:text-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                  <span className="text-sm font-medium text-foreground truncate">
                    {conversations.find(c => c.conversation_key === activeConversation)
                      ? titleFor(conversations.find(c => c.conversation_key === activeConversation)!)
                      : "Conversation"}
                  </span>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto px-3 py-2 space-y-2">
                  {[...messages].reverse().map((msg) => {
                    const isMine = msg.sender.id === auth.user?.id;
                    return (
                      <div
                        key={msg.id}
                        className={`flex ${isMine ? "justify-end" : "justify-start"}`}
                      >
                        <div
                          className={`max-w-[85%] rounded-2xl px-3 py-1.5 text-xs ${
                            isMine
                              ? "bg-accent text-accent-foreground rounded-br-md"
                              : "bg-surface2 text-foreground rounded-bl-md"
                          }`}
                        >
                          {msg.body}
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>

                {/* Reply input */}
                <div className="px-3 py-2 border-t border-border">
                  <div className="flex items-end gap-2">
                    <div className="flex-1 rounded-2xl bg-surface2 px-3 py-1.5">
                      <textarea
                        ref={textareaRef}
                        value={replyBody}
                        onChange={(e) => {
                          setReplyBody(e.target.value);
                          handleTextareaInput();
                        }}
                        placeholder="Type a message..."
                        className="w-full bg-transparent text-xs text-foreground outline-none placeholder:text-muted resize-none"
                        rows={1}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            handleSendReply();
                          }
                        }}
                      />
                    </div>
                    <button
                      onClick={handleSendReply}
                      disabled={!replyBody.trim() || sending}
                      className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-all ${
                        replyBody.trim() && !sending
                          ? "bg-accent text-accent-foreground"
                          : "bg-surface2 text-muted cursor-not-allowed"
                      }`}
                    >
                      <Send className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              // Compose view
              <div className="flex flex-col h-full">
                {/* Back button and title */}
                <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
                  <button
                    onClick={() => setView("conversations")}
                    className="rounded-lg p-1 text-muted hover:text-foreground"
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </button>
                  <span className="text-sm font-medium text-foreground">New Message</span>
                </div>

                {composeDataLoading ? (
                  <div className="flex items-center justify-center flex-1">
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-border border-t-accent" />
                  </div>
                ) : (
                  <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
                    {/* Message type toggle */}
                    <div className="flex gap-1 rounded-lg bg-surface2 p-0.5">
                      <button
                        onClick={() => {
                          setMessageType("child");
                          setSelectedClassroomId(null);
                        }}
                        className={`flex-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${
                          messageType === "child"
                            ? "bg-surface text-foreground shadow-sm"
                            : "text-muted hover:text-foreground"
                        }`}
                      >
                        Individual
                      </button>
                      <button
                        onClick={() => {
                          setMessageType("broadcast");
                          setSelectedRecipientType(null);
                          setSelectedRecipientLabel("");
                          setSelectedChildId(null);
                          setSelectedParentUserId(null);
                          setSearchQuery("");
                        }}
                        className={`flex-1 flex items-center justify-center gap-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${
                          messageType === "broadcast"
                            ? "bg-surface text-foreground shadow-sm"
                            : "text-muted hover:text-foreground"
                        }`}
                      >
                        <Users className="h-3 w-3" />
                        Broadcast
                      </button>
                    </div>

                    {/* Recipient selection */}
                    {messageType === "child" ? (
                      <div ref={searchRef} className="relative">
                        <label className="mb-1 block text-xs font-medium text-muted">
                          To
                        </label>
                        {selectedRecipientType ? (
                          <div className="flex items-center gap-1.5 rounded-lg bg-surface2 px-2.5 py-1.5">
                            <span className={`shrink-0 rounded px-1 py-0.5 text-[10px] font-medium ${
                              selectedRecipientType === "child"
                                ? "bg-accent/15 text-accent"
                                : "bg-[#7A8C6B]/15 text-[#7A8C6B]"
                            }`}>
                              {selectedRecipientType === "child" ? "Child" : "Parent"}
                            </span>
                            <span className="text-xs text-foreground truncate flex-1">
                              {selectedRecipientLabel}
                            </span>
                            <button
                              onClick={() => {
                                setSelectedRecipientType(null);
                                setSelectedRecipientLabel("");
                                setSelectedChildId(null);
                                setSelectedParentUserId(null);
                                setSearchQuery("");
                              }}
                              className="shrink-0 rounded p-0.5 text-muted hover:text-foreground"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ) : (
                          <>
                            <div className="relative">
                              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted" />
                              <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => {
                                  setSearchQuery(e.target.value);
                                  setShowSearchResults(true);
                                }}
                                onFocus={() => setShowSearchResults(true)}
                                placeholder="Search child or parent name..."
                                className="w-full rounded-lg bg-surface2 pl-7 pr-2.5 py-1.5 text-xs text-foreground outline-none placeholder:text-muted focus:ring-1 focus:ring-accent/30"
                              />
                            </div>
                            {showSearchResults && searchQuery.trim() && (
                              <div className="absolute left-0 right-0 top-full mt-1 z-10 max-h-40 overflow-y-auto rounded-lg bg-surface border border-border shadow-soft">
                                {searchResults.length === 0 ? (
                                  <div className="px-2.5 py-2 text-xs text-muted">
                                    No results found
                                  </div>
                                ) : (
                                  searchResults.map((result) => (
                                    <button
                                      key={`${result.type}-${result.id}`}
                                      onClick={() => {
                                        setSelectedRecipientType(result.type);
                                        setSelectedRecipientLabel(result.label);
                                        if (result.type === "child") {
                                          setSelectedChildId(result.id);
                                          setSelectedParentUserId(null);
                                        } else {
                                          setSelectedParentUserId(result.id);
                                          setSelectedChildId(null);
                                        }
                                        setSearchQuery("");
                                        setShowSearchResults(false);
                                      }}
                                      className="flex w-full items-start gap-2 px-2.5 py-1.5 text-left hover:bg-surface2 transition-colors"
                                    >
                                      <span className={`mt-0.5 shrink-0 rounded px-1 py-0.5 text-[10px] font-medium ${
                                        result.type === "child"
                                          ? "bg-accent/15 text-accent"
                                          : "bg-[#7A8C6B]/15 text-[#7A8C6B]"
                                      }`}>
                                        {result.type === "child" ? "Child" : "Parent"}
                                      </span>
                                      <div className="min-w-0 flex-1">
                                        <div className="text-xs font-medium text-foreground truncate">
                                          {result.label}
                                        </div>
                                        <div className="text-[10px] text-muted truncate">
                                          {result.subtitle}
                                        </div>
                                      </div>
                                    </button>
                                  ))
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    ) : (
                      <div>
                        <label className="mb-1 block text-xs font-medium text-muted">
                          Select Classroom
                        </label>
                        <select
                          value={selectedClassroomId ?? ""}
                          onChange={(e) => setSelectedClassroomId(e.target.value ? Number(e.target.value) : null)}
                          className="w-full rounded-lg bg-surface2 px-2.5 py-1.5 text-xs text-foreground outline-none focus:ring-1 focus:ring-accent/30"
                        >
                          <option value="">Choose a classroom...</option>
                          {filteredClassrooms.map((classroom) => (
                            <option key={classroom.id} value={classroom.id}>
                              {classroom.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* Message body */}
                    <div>
                      <label className="mb-1 block text-xs font-medium text-muted">
                        Message
                      </label>
                      <textarea
                        value={composeBody}
                        onChange={(e) => setComposeBody(e.target.value)}
                        placeholder="Type your message..."
                        rows={5}
                        maxLength={2000}
                        className="w-full resize-none rounded-lg bg-surface2 px-2.5 py-1.5 text-xs text-foreground outline-none placeholder:text-muted focus:ring-1 focus:ring-accent/30"
                      />
                      <p className="mt-0.5 text-[10px] text-muted text-right">
                        {composeBody.length}/2000
                      </p>
                    </div>

                    {/* Error */}
                    {composeError && (
                      <p className="text-[11px] text-danger">{composeError}</p>
                    )}
                  </div>
                )}

                {/* Send button */}
                {!composeDataLoading && (
                  <div className="px-3 py-2 border-t border-border">
                    <button
                      onClick={sendComposedMessage}
                      disabled={composeSending || !composeBody.trim()}
                      className={`flex w-full items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium transition-all ${
                        composeBody.trim() && !composeSending
                          ? "bg-accent text-accent-foreground hover:-translate-y-0.5"
                          : "bg-surface2 text-muted cursor-not-allowed"
                      }`}
                    >
                      <Send className="h-3 w-3" />
                      {composeSending ? "Sending..." : "Send Message"}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

        </div>
      )}
    </div>
  );
}
